import React, { useState } from "react";
import { Grid, Box, Typography, TextField as TF, Button } from "@mui/material";
import Lottie from "react-lottie";
import contact from "../../assets/lottiefiles/contact_us.json";
import { makeStyles } from "@mui/styles";
import { size, COLORS } from "../../utils";
import styles from "./EnquireForm.module.css";

const useStyles = makeStyles((theme) => ({
  cssOutlinedInput: {
    "&$cssFocused $notchedOutline": {
      borderColor: `${COLORS.C1}E0 !important`,
    },
  },

  cssFocused: {
    borderColor: `${COLORS.C1}E0 !important`,
  },

  notchedOutline: {
    borderWidth: size({ p: 0.08 }),
    borderColor: `${COLORS.C1} !important`,
  },
}));

function TextField({ label, tf_props }) {
  const classes = useStyles();
  return (
    <Grid item container direction={"column"}>
      <Grid item>
        <Typography
          sx={{
            fontFamily: "Space Mono",
            fontSize: size({ p: 1 }),
            fontWeight: 400,
            wordSpacing: size({ p: 0.2, neg: true }),
            marginBottom: size({ p: 0.5 }),
            color: COLORS.C1,
          }}
        >
          {label}
        </Typography>
      </Grid>
      <Grid item>
        <TF
          placeholder={label}
          {...tf_props}
          InputProps={{
            style: {
              fontFamily: "Space Mono",
              fontSize: size({ p: 0.8 }),
              fontWeight: 400,
              color: COLORS.C1,
            },
            classes: {
              root: classes.cssOutlinedInput,
              focused: classes.cssFocused,
              notchedOutline: classes.notchedOutline,
            },
          }}
        />
      </Grid>
    </Grid>
  );
}

export default function EnquireForm() {
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus("");

    const formData = new FormData(e.target);


    // BODY TEXT (same as your backend requires)
    const bodyText = `
Full Name: ${formData.get("fullName")}
Mobile Number: ${formData.get("mobileNumber")}
Email: ${formData.get("email")}
Message: ${formData.get("message")}
  `;

    // FINAL PAYLOAD
    const payload = {
      receiver_email: "funnybeat007@gmail.com",
      subject: `New Enquiry Message`,
      body: bodyText,
      base64_data: null,
      filename: null,
    };

    try {
      const res = await fetch("http://api.thinkgrail.com/genesysemailapi/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setStatus("Form submitted successfully!");
        e.target.reset();
      } else {
        setStatus("Failed to submit form. Please try again.");
      }
    } catch (error) {
      setStatus("An error occurred. Please try again later.");
    }

    setLoading(false);
  };

  return (
    <Grid
      container
      flexWrap={"nowrap"}
      alignItems={"center"}
      justifyContent={"start"}
      alignContent={"center"}
      width={size({ p: 100 })}
      height={size({ p: 92, h: true })}
      className={styles["enquire-container"]}
    >
      <Grid item width={size({ p: 50 })} className={styles["lottie-container"]}>
        <Lottie
          width={size({ p: 50 })}
          height={size({ p: 50 })}
          options={{
            loop: true,
            autoplay: true,
            animationData: contact,
          }}
          className={styles["lottie"]}
        />
      </Grid>
      <Grid
        item
        container
        flexWrap={"nowrap"}
        direction={"column"}
        width={size({ p: 40 })}
        justifyContent={"center"}
        rowGap={size({ p: 4, h: true })}
        className={styles["form-container"]}
        component="form"
        onSubmit={handleSubmit}
      >
        <Grid item container direction={"column"}>
          <Grid item>
            <Typography
              sx={{
                fontFamily: "Space Mono",
                fontSize: size({ p: 2.5 }),
                fontWeight: 700,
                textTransform: "uppercase",
                color: `${COLORS.C1}`,
                wordSpacing: size({ p: 0.2, neg: true }),
                letterSpacing: size({ p: 0.1, neg: true }),
              }}
              className={styles["form-heading"]}
            >
              We'd love to
              <Box
                component="span"
                sx={{
                  color: `${COLORS.C3}`,
                }}
              >
                {" help you"}
              </Box>
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              sx={{
                color: `${COLORS.C1}`,
                fontSize: size({ p: 1.1 }),
                fontFamily: "Space Mono",
                textTransform: "none",
                fontWeight: 400,
              }}
              className={styles["form-subheading"]}
            >
              {"Reach out and we’ll get back to you soon."}
            </Typography>
          </Grid>
        </Grid>
        <Grid
          item
          container
          direction={"column"}
          rowGap={size({ p: 1.5, h: true })}
        >
          <Grid item container flexWrap={"nowrap"} columnGap={size({ p: 1 })}>
            <TextField
              tf_props={{
                placeholder: "Enter your Full Name",
                fullWidth: true,
                required: true,
              }}
              className={styles["labels"]}
            />
            <TextField
              tf_props={{
                placeholder: "Enter your Mobile Number",
                name: "mobileNumber",
                fullWidth: true,
                required: true,
              }}
              className={styles["labels"]}
            />
          </Grid>
          <Grid item>
            <TextField
              tf_props={{
                placeholder: "Enter your Email Address",
                name: "email",
                type: "email",
                fullWidth: true,
                required: true,
              }}
              className={styles["labels"]}
            />
          </Grid>
          <Grid item>
            <TextField
              tf_props={{
                name: "message",
                multiline: true,
                fullWidth: true,
                rows: 5,
                placeholder: "Leave us a message...",
                required: true,
              }}
              className={styles["label-4"]}
            />
          </Grid>
        </Grid>
        <Grid item>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              backgroundColor: `${COLORS.C2}E0`,
              fontFamily: "Space Mono",
              fontWeight: 400,
              fontSize: size({ p: 1.3 }),
              color: `${COLORS.WHITE}`,
              textTransform: "none",
              ":hover": {
                backgroundColor: `${COLORS.C2}E0`,
              },
            }}
            className={styles["form-button"]}
          >
            {loading ? "Sending..." : "Send Message"}
          </Button>
        </Grid>
        {status && (
          <Grid item>
            <Typography
              sx={{
                color: status.includes("success") ? "green" : "red",
                fontFamily: "Space Mono",
                fontWeight: 500,
                fontSize: size({ p: 1 }),
                marginTop: size({ p: 1 }),
                textAlign: 'center'
              }}
            >
              {status}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
